/**
 * Admin Audit oversight panel. Lists audit entries — jailbreak/extraction attempts
 * (securityAudit) and config changes (configAudit) — newest first.
 *
 * RENDER GATE: mounted only inside AdminPanel (admin-claim gated); the Worker
 * enforces the admin claim on the request. Entries carry only the bounded audit
 * fields — never keys/config/secrets.
 */

import { useAdminAudit, type AdminHooksDeps } from "./hooks.js";

export interface AuditPanelProps {
  deps: AdminHooksDeps;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AuditPanel({ deps }: AuditPanelProps): JSX.Element {
  const { entries, loading, error, reload } = useAdminAudit(deps);

  if (loading && entries.length === 0) {
    return (
      <p role="status" aria-live="polite" className="flex items-center gap-3 text-sm text-zinc-400">
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-400"
        />
        Cargando registro de auditoría…
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  }

  return (
    <section aria-label="admin-audit" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-zinc-100">Auditoría de seguridad y configuración</h2>
        <button
          type="button"
          aria-label="reload-audit"
          className="min-h-[44px] rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800"
          onClick={() => void reload()}
        >
          Recargar
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-800 px-4 py-8 text-center text-sm text-zinc-500">
          No hay entradas de auditoría.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-950/60 text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Cuándo</th>
                <th className="px-3 py-2 font-medium">Origen</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Usuario</th>
                <th className="px-3 py-2 font-medium">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {entries.map((e) => (
                <tr
                  key={e.id}
                  data-testid={`audit-row-${e.id}`}
                  className="text-zinc-300 transition-colors hover:bg-zinc-800/40"
                >
                  <td className="whitespace-nowrap px-3 py-2 text-zinc-400">{formatDate(e.createdAt)}</td>
                  <td data-testid={`audit-source-${e.id}`} className="px-3 py-2">
                    <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs">{e.source}</span>
                  </td>
                  <td className="px-3 py-2 text-zinc-200">{e.kind}</td>
                  <td className="px-3 py-2 text-zinc-400">{e.uid ?? "—"}</td>
                  <td className="px-3 py-2 text-zinc-400">{e.snippet ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
